/**
 * The one native speech recognizer (iOS SFSpeechRecognizer, Android SpeechRecognizer) through
 * `@capgo/capacitor-speech-recognition`, shared by every dictation mic and by voice calls.
 *
 * The plugin is a singleton: one session at a time, events broadcast to every listener. So this module owns
 * it, and consumers talk to it through an owner token:
 * - Only the owner that started a session can stop or cancel it, so one composer unmounting never kills
 *   another's recording. A different owner's `start` preempts the live session and hands its text back to
 *   its owner (`reason: 'preempted'`).
 * - Listeners are registered per session and stay until the native `stopped` lands, so the final words are
 *   not lost: `stop` lets a trailing partial settle, snapshots the plugin's last partial (iOS clears it on
 *   stop), stops, then waits for `stopped` (≤ 1.2 s) before reading the text.
 * - A partial that isn't a revision of the previous one means the recognizer started a new segment: the
 *   previous text is committed and kept.
 * - When the recognizer stops by itself (silence, a final result, an error) the owner gets `onEnd` with
 *   `auto: true` and the text heard so far.
 * - A failed start is cleaned up (forceStop, listeners removed) so it never leaves the mic on.
 */
import { Capacitor, type PermissionState, type PluginListenerHandle } from '@capacitor/core'
import type {
    SpeechRecognitionAudioLevelEvent,
    SpeechRecognitionErrorEvent,
    SpeechRecognitionListeningEvent,
    SpeechRecognitionPartialResultEvent,
    SpeechRecognitionPlugin,
} from '@capgo/capacitor-speech-recognition'
// Static on purpose (see useVoiceDictation): a lazy chunk can hang under Capacitor's custom scheme.
import { SpeechRecognition } from '@capgo/capacitor-speech-recognition'

export type SpeechPlugin = Pick<
    SpeechRecognitionPlugin,
    'isOnDeviceRecognitionAvailable' | 'start' | 'stop' | 'forceStop' | 'getLastPartialResult' | 'checkPermissions'
    | 'requestPermissions' | 'addListener'
>

export type SpeechErrorKind = 'permission' | 'no-speech' | 'network' | 'busy' | 'unavailable' | 'other'

export interface SpeechError {
    kind: SpeechErrorKind
    code: string
    message: string
}

/** `user`: the owner stopped it. `silence` / `final` / `error`: the recognizer stopped by itself. */
export type SpeechEndReason = 'user' | 'silence' | 'final' | 'error' | 'cancelled' | 'preempted'

export interface SpeechEnd {
    text: string
    reason: SpeechEndReason
    /** True when the owner didn't ask for it (the recognizer stopped, or another owner took the mic). */
    auto: boolean
    error: SpeechError | null
}

export interface SpeechHandlers {
    /** The whole transcript so far (committed segments + the live partial). */
    onText?: (text: string) => void
    /** Input level, 0..1, about 15 times a second. */
    onLevel?: (level: number) => void
    onEnd?: (end: SpeechEnd) => void
}

export interface SpeechStartOptions {
    /** BCP 47 tag; defaults to the device language (`navigator.language`). */
    language?: string
    /** Names the recognizer should favour (iOS SFSpeechRecognizer only). */
    contextualStrings?: string[]
}

export interface SpeechOwner {
    readonly id: number
    readonly label: string
}

export class SpeechStartError extends Error {
    constructor(public readonly error: SpeechError) {
        super(error.message)
    }
}

export interface NativeSpeech {
    owner(label: string): SpeechOwner
    isActive(owner: SpeechOwner): boolean
    /** Speech + microphone permission, without prompting. */
    permission(): Promise<PermissionState>
    /** Prompts when needed. Resolves 'granted' or 'denied'. */
    ensurePermission(): Promise<'granted' | 'denied'>
    /** Starts a session for `owner` (preempting anyone else's). Rejects with `SpeechStartError`. */
    start(owner: SpeechOwner, handlers?: SpeechHandlers, opts?: SpeechStartOptions): Promise<void>
    /** Stops `owner`'s session and resolves the final text ('' when it isn't theirs). */
    stop(owner: SpeechOwner): Promise<string>
    /** Drops `owner`'s session without a transcript. A no-op when it isn't theirs. */
    cancel(owner: SpeechOwner): Promise<void>
}

export interface NativeSpeechEnv {
    platform?: () => string
    language?: () => string
    now?: () => number
    /** How long a trailing partial may take to land before stopping. */
    settleMs?: number
    /** How long to wait for the native `stopped` after stop. */
    stopWaitMs?: number
    startTimeoutMs?: number
}

/** Above this the input is speech (native levels map about -50..0 dBFS to 0..1). */
const VOICE_LEVEL = 0.3

// ── pure helpers (exported for tests) ──────────────────────────────────────────

function words(t: string): string[] {
    return t.toLowerCase().replace(/[^\p{L}\p{N}'\s]/gu, ' ').split(/\s+/).filter(Boolean)
}

/** Is `next` the recognizer revising `prev` (same utterance), rather than a fresh segment? */
export function isRevision(prev: string, next: string): boolean {
    const a = words(prev)
    const b = words(next)
    if (!a.length || !b.length) return true
    if (a[0] === b[0]) return true
    const seen = new Set(b)
    const shared = a.filter((w) => seen.has(w)).length
    return shared / a.length >= 0.5 && b.length >= Math.ceil(a.length * 0.5)
}

export function joinText(a: string, b: string): string {
    const x = a.trim()
    const y = b.trim()
    return x && y ? `${x} ${y}` : x || y
}

const ERROR_TEXT: Record<SpeechErrorKind, string> = {
    permission: 'Microphone access is off.',
    'no-speech': "I didn't catch anything. Try again.",
    network: 'Speech recognition needs a connection right now. Check your network and try again.',
    busy: 'The microphone is busy. Try again in a moment.',
    unavailable: "Speech recognition isn't available right now.",
    other: 'Dictation stopped unexpectedly. Try again.',
}

/** Plugin error codes (iOS `DOMAIN_CODE`, Android `SpeechRecognizer.ERROR_*` names) to a kind and plain words. */
export function classifySpeechError(code: string | undefined, message = ''): SpeechError {
    const c = (code ?? '').toUpperCase()
    const m = message || ''
    let kind: SpeechErrorKind = 'other'
    if (/PERMISSION|NOT_AUTHORIZED|DENIED/.test(c) || /permission|denied|not authori[sz]ed/i.test(m)) kind = 'permission'
    else if (/NO_MATCH|SPEECH_TIMEOUT|_1110$|_216$|_301$|_203$/.test(c) || /no speech|no match|canceled|cancelled/i.test(m)) kind = 'no-speech'
    else if (/NETWORK|SERVER/.test(c) || /network|offline|connection/i.test(m)) kind = 'network'
    else if (/BUSY/.test(c) || /already running|busy/i.test(m)) kind = 'busy'
    else if (/UNAVAILABLE|UNSUPPORTED|MODEL_DOWNLOAD|AUDIO|NOT_AVAILABLE/.test(c) || /not available|unavailable|unsupported/i.test(m)) kind = 'unavailable'
    return { kind, code: c || 'UNKNOWN', message: ERROR_TEXT[kind] }
}

function delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms))
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(message)), ms)
        p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
    })
}

function errText(e: unknown): string {
    return e instanceof Error ? e.message : typeof e === 'string' ? e : ''
}

// ── the controller ─────────────────────────────────────────────────────────────

interface Session {
    owner: SpeechOwner
    handlers: SpeechHandlers
    state: 'starting' | 'live' | 'stopping' | 'ended'
    nativeId: number | null
    committed: string
    current: string
    lastTextAt: number
    lastVoiceAt: number
    error: SpeechError | null
    /** A `stopped` that landed before start() resolved. */
    earlyStop: { reason?: string; errorCode?: string } | null
    nativeStopped: boolean
    onStopped: Array<() => void>
    handles: PluginListenerHandle[]
    torn: boolean
    release: () => void
    result: SpeechEnd | null
    ending: Promise<SpeechEnd> | null
    errorTimer: ReturnType<typeof setTimeout> | null
}

export function createNativeSpeech(plugin: SpeechPlugin, env: NativeSpeechEnv = {}): NativeSpeech {
    const now = env.now ?? (() => Date.now())
    const platform = env.platform ?? (() => Capacitor.getPlatform())
    const language = env.language ?? (() => (typeof navigator !== 'undefined' && navigator.language) || 'en-US')
    const settleMs = env.settleMs ?? 450
    const stopWaitMs = env.stopWaitMs ?? 1200
    const startTimeoutMs = env.startTimeoutMs ?? 8000

    let ownerSeq = 0
    let active: Session | null = null
    /** Resolves once the previous native session is torn down (the plugin refuses overlapping starts). */
    let idle: Promise<void> = Promise.resolve()
    const onDevice = new Map<string, boolean>()

    const fullText = (s: Session) => joinText(s.committed, s.current)

    function teardown(s: Session): void {
        if (s.torn) return
        s.torn = true
        if (s.errorTimer) clearTimeout(s.errorTimer)
        for (const h of s.handles) void Promise.resolve(h.remove()).catch(() => {})
        s.handles = []
        s.release()
    }

    function finalize(s: Session, end: SpeechEnd): SpeechEnd {
        if (s.result) return s.result
        s.result = end
        s.state = 'ended'
        if (active === s) active = null
        try {
            s.handlers.onEnd?.(end)
        } catch {
            /* a consumer's handler must not break the controller */
        }
        return end
    }

    function nativeReason(reason: string | undefined, error: SpeechError | null): SpeechEndReason {
        if (reason === 'silence') return 'silence'
        if (reason === 'error') return error && error.kind !== 'no-speech' ? 'error' : 'silence'
        return 'final'
    }

    /** The recognizer stopped without being asked: commit what it heard. */
    function autoEnd(s: Session, info: { reason?: string; errorCode?: string }): void {
        if (s.result || s.ending) return
        const error = info.reason === 'error' ? s.error ?? classifySpeechError(info.errorCode) : null
        const reason = nativeReason(info.reason, error)
        finalize(s, { text: fullText(s), reason, auto: true, error: reason === 'error' ? error : null })
    }

    function belongs(s: Session, sessionId: number | undefined): boolean {
        return sessionId == null || s.nativeId == null || sessionId === s.nativeId
    }

    function onPartial(s: Session, e: SpeechRecognitionPartialResultEvent): void {
        if (s.result) return
        if (e.accumulatedText) {
            s.committed = ''
            s.current = e.accumulatedText.trim()
        } else {
            const next = (e.matches?.[0] ?? '').trim()
            if (e.accumulated) s.committed = e.accumulated.trim()
            if (e.isRestarting) {
                if (!e.accumulated) s.committed = joinText(s.committed, next)
                s.current = ''
            } else if (next) {
                if (s.current && !isRevision(s.current, next)) s.committed = joinText(s.committed, s.current)
                s.current = next
            }
        }
        s.lastTextAt = now()
        s.handlers.onText?.(fullText(s))
    }

    function onState(s: Session, e: SpeechRecognitionListeningEvent): void {
        const state = e.state ?? e.status
        if (state === 'startingListening' && s.nativeId == null && e.sessionId != null) s.nativeId = e.sessionId
        if (!belongs(s, e.sessionId)) return
        if (state !== 'stopped') return
        if (s.state === 'starting') {
            s.earlyStop = { reason: e.reason, errorCode: e.errorCode }
            return
        }
        s.nativeStopped = true
        for (const fn of s.onStopped.splice(0)) fn()
        if (s.state === 'live') autoEnd(s, { reason: e.reason, errorCode: e.errorCode })
        teardown(s)
    }

    function onError(s: Session, e: SpeechRecognitionErrorEvent): void {
        if (!belongs(s, e.sessionId)) return
        s.error = classifySpeechError(e.code, e.message)
        // The plugin always follows an error with `stopped`; if it doesn't, end it ourselves.
        if (s.state === 'live' && !s.errorTimer) {
            s.errorTimer = setTimeout(() => {
                if (s.nativeStopped) return
                autoEnd(s, { reason: 'error', errorCode: e.code })
                void plugin.forceStop({ timeout: 300 }).catch(() => {})
                teardown(s)
            }, 1500)
        }
    }

    function onLevel(s: Session, e: SpeechRecognitionAudioLevelEvent): void {
        if (s.result) return
        const level = Math.max(0, Math.min(1, Number(e.level) || 0))
        if (level > VOICE_LEVEL) s.lastVoiceAt = now()
        s.handlers.onLevel?.(level)
    }

    /** Let a trailing partial land before stopping (the user usually taps right after their last word). */
    async function settle(s: Session): Promise<void> {
        const until = now() + settleMs
        const quiet = Math.min(300, settleMs)
        while (now() < until) {
            const t = now()
            if (t - s.lastTextAt >= quiet && t - s.lastVoiceAt >= quiet) return
            await delay(Math.max(10, Math.min(50, quiet / 4)))
        }
    }

    async function lastPartial(): Promise<string> {
        try {
            const r = await withTimeout(plugin.getLastPartialResult(), 300, 'timeout')
            return (r?.text ?? '').trim()
        } catch {
            return ''
        }
    }

    function waitStopped(s: Session, ms: number): Promise<void> {
        if (s.nativeStopped) return Promise.resolve()
        return new Promise<void>((resolve) => {
            const t = setTimeout(resolve, ms)
            s.onStopped.push(() => { clearTimeout(t); resolve() })
        })
    }

    function end(s: Session, reason: 'user' | 'cancelled' | 'preempted'): Promise<SpeechEnd> {
        if (s.result) return Promise.resolve(s.result)
        if (s.ending) return s.ending
        s.ending = (async () => {
            if (reason !== 'cancelled' && s.state === 'live') await settle(s)
            const snapshot = reason === 'cancelled' ? '' : await lastPartial()
            if (s.state !== 'ended') s.state = 'stopping'
            try {
                await withTimeout(plugin.stop(), 1500, 'stop timed out')
            } catch {
                /* already stopped */
            }
            if (reason === 'cancelled') {
                // Don't hold the caller; the next start still waits on `idle` for the native teardown.
                void waitStopped(s, stopWaitMs).then(() => cleanupAfterStop(s))
                return finalize(s, { text: '', reason, auto: false, error: null })
            }
            await waitStopped(s, stopWaitMs)
            const text = fullText(s) || snapshot
            const out = finalize(s, { text, reason, auto: reason === 'preempted', error: null })
            cleanupAfterStop(s)
            return out
        })()
        return s.ending
    }

    function cleanupAfterStop(s: Session): void {
        if (s.torn) return
        if (!s.nativeStopped) void plugin.forceStop({ timeout: 300 }).catch(() => {})
        teardown(s)
    }

    async function useOnDevice(lang: string): Promise<boolean> {
        // iOS only: SFSpeechRecognizer's on-device model (no network, no one-minute cap). Android's on-device
        // recognizer may need a model download first, which would stall the tap; it uses the default service.
        if (platform() !== 'ios') return false
        const hit = onDevice.get(lang)
        if (hit !== undefined) return hit
        let ok = false
        try {
            ok = (await withTimeout(plugin.isOnDeviceRecognitionAvailable({ language: lang, preferLegacyRecognizer: true }), 1500, 'timeout')).available
        } catch {
            ok = false
        }
        onDevice.set(lang, ok)
        return ok
    }

    async function startNative(s: Session, opts: SpeechStartOptions): Promise<void> {
        const lang = opts.language || language()
        const base = {
            language: lang, partialResults: true, popup: false, maxResults: 1, addPunctuation: true, muteRecognizerBeep: true,
            ...(opts.contextualStrings?.length ? { contextualStrings: opts.contextualStrings } : {}),
        }
        const attempt = (extra: Record<string, unknown>) => {
            s.nativeId = null
            s.earlyStop = null
            return withTimeout(plugin.start({ ...base, ...extra }), startTimeoutMs, 'Could not start the microphone.')
        }
        const onDev = await useOnDevice(lang)
        try {
            await attempt(onDev ? { useOnDeviceRecognition: true, preferLegacyRecognizer: true } : {})
        } catch (e) {
            const msg = errText(e)
            if (onDev) {
                onDevice.set(lang, false)
                await plugin.forceStop({ timeout: 300 }).catch(() => {})
                await delay(150)
                await attempt({})
            } else if (/already running/i.test(msg)) {
                await plugin.forceStop({ timeout: 300 }).catch(() => {})
                await delay(300)
                await attempt({})
            } else {
                throw e
            }
        }
    }

    async function start(owner: SpeechOwner, handlers: SpeechHandlers = {}, opts: SpeechStartOptions = {}): Promise<void> {
        const prev = active
        if (prev) await end(prev, prev.owner.id === owner.id ? 'cancelled' : 'preempted')
        await Promise.race([idle, delay(stopWaitMs + 300)])
        let release = () => {}
        idle = new Promise<void>((r) => (release = r))
        const s: Session = {
            owner, handlers, state: 'starting', nativeId: null, committed: '', current: '', lastTextAt: 0, lastVoiceAt: 0,
            error: null, earlyStop: null, nativeStopped: false, onStopped: [], handles: [], torn: false, release,
            result: null, ending: null, errorTimer: null,
        }
        active = s
        try {
            s.handles = await Promise.all([
                plugin.addListener('partialResults', (e) => onPartial(s, e)),
                plugin.addListener('listeningState', (e) => onState(s, e)),
                plugin.addListener('error', (e) => onError(s, e)),
                plugin.addListener('audioLevel', (e) => onLevel(s, e)),
            ])
            if (active !== s) throw new SpeechStartError({ kind: 'busy', code: 'PREEMPTED', message: ERROR_TEXT.busy })
            await startNative(s, opts)
        } catch (e) {
            if (active === s) active = null
            s.state = 'ended'
            await plugin.forceStop({ timeout: 300 }).catch(() => {})
            teardown(s)
            if (e instanceof SpeechStartError) throw e
            throw new SpeechStartError(s.error ?? classifySpeechError(undefined, errText(e)))
        }
        if (s.result || s.ending) {
            // Stopped or preempted while it was spinning up: make sure the native session doesn't linger.
            if (!s.nativeStopped) await plugin.stop().catch(() => {})
            return
        }
        s.state = 'live'
        if (s.earlyStop) {
            s.nativeStopped = true
            autoEnd(s, s.earlyStop)
            teardown(s)
        }
    }

    return {
        owner: (label) => ({ id: ++ownerSeq, label }),
        isActive: (owner) => active?.owner.id === owner.id,
        async permission() {
            const r = await withTimeout(plugin.checkPermissions(), 8000, 'Could not check microphone permission.')
            return r.speechRecognition
        },
        async ensurePermission() {
            const r = await withTimeout(plugin.checkPermissions(), 8000, 'Could not check microphone permission.')
            if (r.speechRecognition === 'granted') return 'granted'
            if (r.speechRecognition === 'denied') return 'denied'
            const asked = await withTimeout(plugin.requestPermissions(), 60_000, 'Microphone permission request timed out.')
            return asked.speechRecognition === 'granted' ? 'granted' : 'denied'
        },
        start,
        async stop(owner) {
            const s = active
            if (!s || s.owner.id !== owner.id) return ''
            return (await end(s, 'user')).text
        },
        async cancel(owner) {
            const s = active
            if (!s || s.owner.id !== owner.id) return
            await end(s, 'cancelled')
        },
    }
}

let shared: NativeSpeech | null = null

/** The app-wide controller over the real plugin. */
export function nativeSpeech(): NativeSpeech {
    if (!shared) shared = createNativeSpeech(SpeechRecognition as unknown as SpeechPlugin)
    return shared
}

/** Native recognition is the dictation / call path in the app (never on the web build). */
export function nativeSpeechSupported(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SpeechRecognition')
}
